/**
 * GIS-D04 — coordEditsService
 *
 * Cliente para el Worker POST/GET /coord-edits.
 * Registra una edición manual de coordenadas en PostGIS vía Cloudflare Worker.
 *
 * Prereqs: PG-GA04 (POST) + PG-GA05 (GET) desplegados ✅
 *
 * SEC0-c1 (DOP-1 a): llama a `WORKER_BASE` fijo (`geodata.pteltool.es`), como
 * los correos desde `SEC0-b2b`; `VITE_WORKER_URL` ya no cuenta. El Worker exige
 * correo verificado y rol (DOP-2 a) y responde 403 con `code: 'SIN_PERMISO'`.
 */

import { WORKER_BASE } from '@/lib/config/workerEndpoints';

/** Texto para quien no tiene permiso (visor, sin verificar, editor sin ese municipio). */
export const MENSAJE_SIN_PERMISO =
  'Tu cuenta no tiene permiso para guardar coordenadas de este municipio';

// ============================================================================
// TYPES
// ============================================================================

export interface CoordEditPayload {
  /** ID de la infraestructura (string, ej. "42") */
  infraId: string;
  /** Nombre legible de la infraestructura */
  infraNombre: string;
  /** Código INE 5 dígitos del municipio */
  ine5: string;
  /** Coordenadas anteriores (null si no las había) */
  geomBefore: { x: number; y: number } | null;
  /** Coordenadas confirmadas (obligatorio) */
  geomAfter: { x: number; y: number };
  /** Origen de la edición: 'manual' | 'assisted' | 'map' */
  editSource: 'manual' | 'assisted' | 'map';
}

export interface CoordEditResult {
  /** UUID de la edición insertada en PostGIS */
  id: string;
  ok: true;
}

export class CoordEditError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'CoordEditError';
  }
}

// ============================================================================
// POST /coord-edits
// ============================================================================

/**
 * Registra una edición de coordenadas en PostGIS.
 *
 * @param payload  — campos de la edición
 * @param idToken  — Firebase ID token del usuario autenticado
 * @returns UUID de la fila insertada
 * @throws CoordEditError si el Worker devuelve error HTTP
 */
export async function postCoordEdit(
  payload: CoordEditPayload,
  idToken: string,
): Promise<CoordEditResult> {
  const url = `${WORKER_BASE}/coord-edits`;

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    let detail: unknown;
    try { detail = await res.json(); } catch { /* sin body */ }
    const sinPermiso =
      res.status === 403 &&
      (detail as { code?: unknown } | undefined)?.code === 'SIN_PERMISO';
    throw new CoordEditError(
      sinPermiso ? MENSAJE_SIN_PERMISO : `Worker error ${res.status}`,
      res.status,
      detail,
    );
  }

  const data = await res.json() as { id: string };
  return { id: data.id, ok: true };
}
